/**
 * Configuration building and validation for the SDK.
 *
 * @module
 */

import {
  assert,
  type,
  optional,
  string,
  number,
  nullable,
  boolean,
  enums,
  object,
  union,
  record,
  array,
  unknown,
  type Infer,
} from 'superstruct';
import type { AppConfig, CreatePypeLoaderOptions } from './types';

/** User identity — all fields optional. */
const UserDataStruct = object({
  firstName: optional(string()),
  lastName: optional(string()),
  email: optional(string()),
  phone: optional(string()),
  screenName: optional(string()),
  token: optional(string()),
});

/** Opaque passthrough — string, object, or `[{ name, value }]` array. */
const PassthroughStruct = union([
  string(),
  record(string(), unknown()),
  array(unknown()),
]);

/** Host-supplied first message(s) shown while connecting. */
const InitialMessagesStruct = object({
  welcomeMessage: optional(string()),
  showThinkingBubble: optional(boolean()),
  persistent: optional(boolean()),
});

/** Floating panel frame size — mirrors the widget's `shell.size`. */
const ShellSizeStruct = object({
  width: number(),
  height: number(),
  lockRatio: boolean(),
  position: optional(string()),
});

/** Popup tuning for the `window` render target. */
const WindowOptionsStruct = object({
  name: optional(string()),
  width: optional(number()),
  height: optional(number()),
  features: optional(string()),
  bootstrapUrl: optional(string()),
});

const AppConfigStruct = type({
  appId: optional(nullable(string())),
  language: string(),
  theme: optional(nullable(string())),
  launcherButtonText: optional(nullable(string())),
  launcherButtonSize: optional(nullable(string())),
  launcherPosition: optional(nullable(string())),
  launcherVariant: optional(nullable(string())),
  customerLogoUrl: optional(nullable(string())),
  customerName: optional(nullable(string())),
  user: optional(UserDataStruct),
  passthrough: optional(PassthroughStruct),
  initialMessages: optional(InitialMessagesStruct),
  showInlineLoader: optional(boolean()),
  disableSlideAnimation: optional(boolean()),
  debug: optional(boolean()),
  useIframe: optional(boolean()),
  modality: optional(enums(['floating', 'fullpage'])),
  size: optional(ShellSizeStruct),
  anchorTo: optional(enums(['viewport', 'container'])),
  launcher: optional(enums(['default', 'none'])),
  target: optional(enums(['inline', 'window'])),
  window: optional(WindowOptionsStruct),
  preview: optional(boolean()),
  skipDesignFetch: optional(boolean()),
  engagementApiUrl: optional(nullable(string())),
});

/**
 * What `normalizeAppConfig` accepts: the validated raw object OR the host's flat
 * options. They overlap on every field; the union is what lets one mapping
 * serve both callers.
 */
type AppConfigInput = Infer<typeof AppConfigStruct> | CreatePypeLoaderOptions;

/**
 * The ONE field mapping. `validateAppConfig` and `buildAppConfig` were two
 * hand-written lists of the same ~25 fields with the same defaults, plus the
 * schema above — three places to edit for one added field, and nothing said so.
 * They now differ only in how they get here.
 *
 * Written as an explicit object rather than a spread so every default is
 * visible, and so a field present in `AppConfig` but missing here is a type
 * error instead of an `undefined` that surfaces at runtime.
 */
function normalizeAppConfig(data: AppConfigInput): AppConfig {
  return {
    appId: data.appId ?? null,
    language: data.language ?? 'en',
    theme: data.theme ?? null,
    launcherButtonText: data.launcherButtonText ?? null,
    launcherButtonSize: data.launcherButtonSize ?? null,
    launcherPosition: data.launcherPosition ?? null,
    launcherVariant: data.launcherVariant ?? null,
    customerLogoUrl: data.customerLogoUrl ?? null,
    customerName: data.customerName ?? null,
    user: data.user,
    // Opaque — validated as string | object | array, carried as-is.
    passthrough: data.passthrough as AppConfig['passthrough'],
    initialMessages: data.initialMessages,
    showInlineLoader: data.showInlineLoader ?? true,
    disableSlideAnimation: data.disableSlideAnimation ?? false,
    debug: data.debug ?? false,
    // `modality` stays `undefined` when unset so the design-merge can apply the
    // saved design's modality (backend-default + host-override). `launcher` and
    // `useIframe` are HOST-CONFIG-ONLY (never from the blob) so they default
    // here — `launcher: 'default'`, `useIframe: false`.
    useIframe: data.useIframe ?? false,
    modality: data.modality,
    // Boot-immutable, designer-owned; carried through for the designer preview
    // (the real widget gets size from the design fetch, not the Loader).
    size: data.size,
    anchorTo: data.anchorTo ?? 'viewport',
    launcher: data.launcher ?? 'default',
    // `target`/`window` are host-config-only (never from the design blob).
    target: data.target ?? 'inline',
    window: data.window,
    preview: data.preview ?? false,
    skipDesignFetch: data.skipDesignFetch ?? false,
    engagementApiUrl: data.engagementApiUrl ?? null,
  };
}

/**
 * Every key the schema accepts, derived from the schema itself.
 *
 * Not a hand-maintained list: superstruct's `type()` exposes its shape, so
 * adding a field to `AppConfigStruct` extends this automatically. A separate
 * constant would be the fourth place to forget.
 */
const APP_CONFIG_KEYS: ReadonlySet<string> = new Set(
  Object.keys(AppConfigStruct.schema)
);

/**
 * Warn — never throw — on a key the schema does not know.
 *
 * `AppConfigStruct` is `type()`, not `object()`, so an unknown key is silently
 * DISCARDED: `launcerPosition` (a typo) validates fine and does nothing.
 * Embed snippets get pasted into a CMS and stay there for years, so a silent
 * discard is the worst outcome — the host sees no error and no effect.
 *
 * Warning rather than rejecting is deliberate in the other direction too: a
 * host page may legitimately carry a key a NEWER loader added and an older
 * bundle does not know, and refusing to boot over that would be a
 * self-inflicted outage.
 */
/**
 * Keys `CreatePypeLoaderOptions` carries that belong to the LOADER, not to the
 * app config — so `warnUnknownKeys` must not flag them.
 *
 * `container` and `onError` are documented, common options: without this every
 * real boot logged `ignoring unknown config key(s): container, onError` on the
 * customer's page, which is both wrong and alarming.
 *
 * `loader-only-keys.test-d.ts` is what keeps this honest — adding an option
 * without classifying it fails `vitest --typecheck` rather than shipping a new
 * spurious warning to every customer page.
 */
export const LOADER_ONLY_KEYS = ['container', 'onError'] as const;

const LOADER_ONLY: ReadonlySet<string> = new Set(LOADER_ONLY_KEYS);

function warnUnknownKeys(
  data: unknown,
  exempt: ReadonlySet<string> = new Set()
): void {
  if (!data || typeof data !== 'object') return;
  const unrecognised = Object.keys(data).filter(
    (k) => !APP_CONFIG_KEYS.has(k) && !exempt.has(k)
  );
  if (unrecognised.length === 0) return;
  console.warn(
    `[pype-loader] ignoring unknown config key(s): ${unrecognised.join(', ')}. ` +
      `Check spelling — an unrecognised key is discarded silently.`
  );
}

/** Validates raw data as {@link AppConfig}. Throws `StructError` if invalid. */
export function validateAppConfig(data: unknown): AppConfig {
  assert(data, AppConfigStruct);
  warnUnknownKeys(data);
  return normalizeAppConfig(data);
}

/**
 * Build an {@link AppConfig} from flat {@link CreatePypeLoaderOptions} properties.
 *
 * Applies sensible defaults for omitted fields:
 * - `language`: `'en'`
 * - `modality`: `'floating'`
 * - `useIframe`: `false`
 * - All other optional fields: `null`
 *
 * @example
 * ```ts
 * const config = buildAppConfig({ appId: 'app_123' });
 * // config.language === 'en' (default)
 * // config.modality === 'floating' (default)
 * ```
 */
export function buildAppConfig(options: CreatePypeLoaderOptions): AppConfig {
  // Exempting the loader's own keys — see LOADER_ONLY_KEYS.
  warnUnknownKeys(options, LOADER_ONLY);
  return normalizeAppConfig(options);
}
